import { Asset, Folder, FolderContent } from '@/api/generatedApi';
import { useListFolderContents } from '@/hooks/api/folders/useListFolderContents';
import { isFolder } from '@/utils/folders';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Box, CircularProgress, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getContentName } from '@/components/modals/BulkMoveContentsModal/utils';
import { useState } from 'react';

const FolderContents = ({ folderId }: { folderId: string }) => {
  const { contents, isLoading } = useListFolderContents({ folderId });

  if (isLoading) {
    return (
      <TreeItem
        itemId={`loading-${folderId}`}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <CircularProgress size={16} />
            <Typography variant='body2' color='text.secondary'>
              Loading...
            </Typography>
          </Box>
        }
      />
    );
  }

  const sorted = sortContents(contents ?? []);

  return (
    <>
      {sorted.map(item => (
        <ContentTreeItem key={itemKey(item)} content={item} />
      ))}
    </>
  );
};

export const ContentTreeItem = ({ content }: { content: FolderContent }) => {
  const name = getContentName(content);

  if (isFolder(content)) {
    return (
      <TreeItem
        itemId={`folder-${content.id}`}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <FolderIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant='body2' noWrap>
              {name}
            </Typography>
          </Box>
        }
      >
        <FolderContents folderId={content.id} />
      </TreeItem>
    );
  }

  return (
    <TreeItem
      itemId={`asset-${content.id}`}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <InsertDriveFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant='body2' noWrap>
            {name}
          </Typography>
        </Box>
      }
    />
  );
};

export const sortContents = (contents: FolderContent[]): FolderContent[] => {
  const folders = contents.filter((content): content is Folder & FolderContent => isFolder(content));
  const assets = contents.filter((content): content is Asset & FolderContent => !isFolder(content));
  folders.sort((a, b) => getContentName(a).localeCompare(getContentName(b)));
  assets.sort((a, b) => getContentName(a).localeCompare(getContentName(b)));
  return [...folders, ...assets];
};

export const itemKey = (content: FolderContent): string =>
  isFolder(content) ? `folder-${content.id}` : `asset-${content.id}`;

export const DeletionContentsTree = ({ folder }: { folder: Folder }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([`folder-${folder.id}`]);

  return (
    <SimpleTreeView
      expandedItems={expandedItems}
      onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
      sx={{
        '& .MuiTreeItem-content': {
          borderRadius: 1,
        },
      }}
    >
      <TreeItem
        itemId={`folder-${folder.id}`}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <FolderIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant='body2' fontWeight={500} noWrap>
              {folder.name}
            </Typography>
          </Box>
        }
      >
        <FolderContents folderId={folder.id} />
      </TreeItem>
    </SimpleTreeView>
  );
};
